// src/components/Admin/JobsManagement.jsx
import React, { useState } from 'react';
import JobFormModal from './JobFormModal';
import { getExperienceLabel } from '../../utils/experienceLevels';

const JobsManagement = ({ jobs, onDeleteJob, onUpdateJob, onAddJob }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [provinceFilter, setProvinceFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [sortField, setSortField] = useState('title');
    const [sortDirection, setSortDirection] = useState('asc');

    const provinces = [...new Set(jobs.map(j => j.province).filter(p => p))];

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortedJobs = () => {
        let sorted = [...jobs];
        sorted.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            
            if (aVal === undefined || aVal === null) aVal = '';
            if (bVal === undefined || bVal === null) bVal = '';
            
            if (sortField === 'closingDate') {
                aVal = aVal ? new Date(aVal) : new Date(0);
                bVal = bVal ? new Date(bVal) : new Date(0);
            } else if (sortField === 'applicationCount') {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
            } else if (sortField === 'experienceLevel') {
                aVal = getExperienceLabel(aVal);
                bVal = getExperienceLabel(bVal);
            } else if (sortField === 'jobType') {
                // Internal first, then external
                const typeOrder = { 'internal': 0, 'external': 1 };
                aVal = typeOrder[aVal] ?? 2;
                bVal = typeOrder[bVal] ?? 2;
            } else {
                aVal = String(aVal).toLowerCase();
                bVal = String(bVal).toLowerCase();
            }
            
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    };

    const filteredJobs = getSortedJobs().filter(job => {
        const matchesSearch = job.title?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesProvince = !provinceFilter || job.province === provinceFilter;
        return matchesSearch && matchesProvince;
    });

    const getSortIcon = (field) => {
        if (sortField !== field) return '↕️';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const handleEdit = (job) => {
        setEditingJob(job);
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setEditingJob(null);
        setIsModalOpen(true);
    };

    const handleSave = async (jobData) => {
        let result;
        if (editingJob) {
            result = await onUpdateJob(editingJob.id, jobData);
        } else {
            result = await onAddJob(jobData);
        }
        if (result.success) {
            setIsModalOpen(false);
            setEditingJob(null);
        }
        return result;
    };

    const provincesList = [
        'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
        'Limpopo', 'Mpumalanga', 'Northern Cape', 'North West', 'Western Cape'
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gradpath-dark flex items-center gap-2">
                    <i className="fas fa-briefcase text-gradpath-green"></i>
                    Manage Jobs
                </h2>
                <button
                    onClick={handleAdd}
                    className="bg-gradpath-green text-white px-4 py-2 rounded-lg hover:bg-gradpath-dark transition-all flex items-center gap-2"
                >
                    <i className="fas fa-plus"></i> Add New Job
                </button>
            </div>

            <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap gap-4">
                <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                />
                <select
                    value={provinceFilter}
                    onChange={(e) => setProvinceFilter(e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                >
                    <option value="">All Provinces</option>
                    {provinces.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('title')}>
                                Title {getSortIcon('title')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('jobType')}>
                                Type {getSortIcon('jobType')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('province')}>
                                Province {getSortIcon('province')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('city')}>
                                City {getSortIcon('city')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('workType')}>
                                Work Type {getSortIcon('workType')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('experienceLevel')}>
                                Experience {getSortIcon('experienceLevel')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('closingDate')}>
                                Closing Date {getSortIcon('closingDate')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('applicationCount')}>
                                Apps {getSortIcon('applicationCount')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('isSponsored')}>
                                Sponsored {getSortIcon('isSponsored')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredJobs.map(job => (
                            <tr key={job.id} className="border-t border-gray-200 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">{job.title}</td>
                                <td className="px-4 py-3 text-sm">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        job.jobType === 'internal' ? 'bg-green-100 text-green-700' : 
                                        job.jobType === 'external' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'
                                    }`}>
                                        {job.jobType || 'External'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm">{job.province || 'N/A'}</td>
                                <td className="px-4 py-3 text-sm">{job.city || 'N/A'}</td>
                                <td className="px-4 py-3 text-sm">{job.workType || 'N/A'}</td>
                                <td className="px-4 py-3 text-sm">
                                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                                        {getExperienceLabel(job.experienceLevel)}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm">{job.closingDate || 'N/A'}</td>
                                <td className="px-4 py-3 text-sm">{job.applicationCount || 0}</td>
                                <td className="px-4 py-3 text-sm">
                                    {job.isSponsored ? (
                                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                            <i className="fas fa-star mr-1"></i> Yes
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-400">No</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <button
                                        onClick={() => handleEdit(job)}
                                        className="text-blue-600 hover:text-blue-800 mr-3"
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                        onClick={() => onDeleteJob(job.id)}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredJobs.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        No jobs found
                    </div>
                )}
            </div>

            <JobFormModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingJob(null);
                }}
                onSave={handleSave}
                initialData={editingJob}
                provinces={provincesList}
            />
        </div>
    );
};

export default JobsManagement;